import type { SensoryEvent, SensoryEventType } from './SensoryEventBus'

type HapticPattern = {
  durationMs: number
  cooldownMs: number
  nativeStyle?: 'light' | 'medium' | 'heavy'
}

type HapticPreferences = {
  enabled: boolean
  reducedMotion: boolean
  tier: 'LOW' | 'HIGH'
}

type HapticsBridge = {
  impact?: (options: { style?: string }) => Promise<void>
  notification?: (options: { type?: string }) => Promise<void>
  vibrate?: (options: { duration?: number }) => Promise<void>
}

const DEFAULT_PREFERENCES: HapticPreferences = {
  enabled: true,
  reducedMotion: false,
  tier: 'LOW',
}

const PATTERNS: Record<SensoryEventType, HapticPattern | null> = {
  MUTATION_TRIGGERED: { durationMs: 12, cooldownMs: 120, nativeStyle: 'light' },
  MUTATION_SUCCESS: { durationMs: 24, cooldownMs: 180, nativeStyle: 'medium' },
  MUTATION_ERROR: { durationMs: 42, cooldownMs: 260, nativeStyle: 'heavy' },
  SCAN_SUCCESS: { durationMs: 28, cooldownMs: 180, nativeStyle: 'medium' },
  SCAN_ERROR: { durationMs: 52, cooldownMs: 260, nativeStyle: 'heavy' },
  RAID_COMPLETE: { durationMs: 60, cooldownMs: 600, nativeStyle: 'heavy' },
  GHOST_ECHO_NAVIGATION: null,
}

class HapticServiceController {
  private preferences = DEFAULT_PREFERENCES
  private lastByType = new Map<SensoryEventType, number>()
  private bridgePromise: Promise<HapticsBridge | null> | null = null

  configure(preferences: Partial<HapticPreferences>): void {
    this.preferences = { ...this.preferences, ...preferences }
  }

  async handle(event: SensoryEvent): Promise<void> {
    if (event.signal?.aborted) return
    const pattern = PATTERNS[event.type]
    if (!pattern || !this.canRun(event.type, pattern)) return

    await this.runPattern(pattern, event.signal)
  }

  private canRun(type: SensoryEventType, pattern: HapticPattern): boolean {
    if (!this.preferences.enabled || this.preferences.reducedMotion) return false
    const now = Date.now()
    const last = this.lastByType.get(type) ?? 0
    if (now - last < pattern.cooldownMs) return false
    this.lastByType.set(type, now)
    return true
  }

  private async resolveBridge(): Promise<HapticsBridge | null> {
    if (!this.bridgePromise) {
      this.bridgePromise = Promise.resolve().then(() => {
        const maybeWindow = typeof window !== 'undefined'
          ? window as Window & { Capacitor?: { Plugins?: { Haptics?: HapticsBridge } } }
          : undefined
        return maybeWindow?.Capacitor?.Plugins?.Haptics ?? null
      })
    }
    return this.bridgePromise
  }

  private async runPattern(pattern: HapticPattern, signal?: AbortSignal): Promise<void> {
    if (signal?.aborted) return
    const bridge = await this.resolveBridge()
    if (signal?.aborted) return

    try {
      if (bridge?.impact) {
        await bridge.impact({ style: pattern.nativeStyle })
        return
      }
      if (bridge?.vibrate) {
        await bridge.vibrate({ duration: pattern.durationMs })
        return
      }
    } catch {
      // Fall through to browser vibration when the native plugin is unavailable.
    }

    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      navigator.vibrate(this.preferences.tier === 'HIGH' ? pattern.durationMs : Math.min(pattern.durationMs, 28))
    }
  }
}

export const HapticService = new HapticServiceController()
